import { LoanCaseDetails } from "../components/LoanCaseDetails.jsx";
import { PensionReformDetails } from "../components/PensionReformDetails.jsx";
import { FlatTaxDetails } from "../components/FlatTaxDetails.jsx";
import { FlatTaxMathDetails } from "../components/FlatTaxMathDetails.jsx";
import { FlatTaxMathOpenendDetails } from "../components/FlatTaxMathOpenendDetails.jsx";
import { BaseRateDetails } from "../components/BaseRateDetails.jsx";
import {
  getPreLobbyMode,
  getPostConfirmPolishMode,
  getReflectionTiming,
  getTreatmentType,
  hasShareableGroupRationale,
  isBaseRateGroup3Condition,
  isBaseRateStaticSyntheticGroupCondition,
  isPasteEnabledNogroupCondition,
  usesBriefInitialRationale,
  suppressesPostConfirmWriteup,
} from "./conditionUtils.js";

const loanDefaultTask = {
  id: "loan-default",
  title: "Loan Default Prediction Task",
  shortTitle: "Loan Default",
  objectiveText:
    "You will be playing this game with other participants. The objective is to make an accurate prediction, which will be determined by taking the majority vote of your group.",
  introPrompt:
    "Imagine you are a loan officer. Based on your knowledge and intuition, what is your best assessment about whether this loan case will result in default or not?",
  caseNotice:
    "This is the case you will discuss with other participants in the upcoming discussion.",
  questionText: "What is your belief?",
  discussionPrompt:
    "Discuss your predictions about whether this loan case will result in default or not. Share your reasoning and consider others' perspectives.",
  finalVotePrompt:
    "After discussing with your group, what is your final prediction about whether this loan case will result in default?",
  detailsTitle: "Loan case details",
  detailsToggle: {
    show: "👁️ Show loan case",
    hide: "🙈 Hide loan case",
    hiddenText: "Loan case hidden. Click \"Show loan case\" to view the details again."
  },
  aiChatTitle: "AI Assistant Discussion",
  aiChatDescription:
    "Discuss your prediction with the AI assistant to clarify your thinking before joining the group. It will take 5-10 seconds for AI assistant to respond.",
  aiWelcomeTemplate: (label) =>
    `Hello! I see you've chosen that this loan case will ${label}. I'm here to help you think through your reasoning. Why did you make this prediction? What factors led you to this conclusion? Feel free to share your thoughts, and I'll ask you some questions to help you organize your thinking.`,
  confirmDescriptionTemplate: (initial) =>
    `Your initial prediction was: ${initial}. Now that you've discussed your reasoning with the AI assistant, please confirm your prediction (you may keep it the same or change it). After confirming, you will proceed to the lobby and wait for other players to form the game.`,
  options: [
    { value: "Default", label: "Default", shortLabel: "Default", buttonLabel: "Default" },
    { value: "Not Default", label: "Not Default", shortLabel: "Not Default", buttonLabel: "Not Default" }
  ],
  quizEnabled: true,
  DetailsComponent: LoanCaseDetails,
};

const pensionReformTask = {
  id: "pension-reform",
  title: "Pension Age Reform — Prediction Task",
  shortTitle: "Pension Reform",
  objectiveText:
    "You will be playing this game with other participants. The objective is to make an accurate prediction, which will be determined by taking the majority vote of your group.",
  introPrompt:
    "Consider the pension reform scenario. Which group showed a larger increase in employment rates at ages 63–65 after the reform?",
  caseNotice:
    "This is the scenario you will discuss with other participants in the upcoming discussion.",
  questionText: "What is your prediction?",
  discussionPrompt:
    "Discuss your predictions about which group showed a larger increase in employment rates at ages 63–65 after the reform. Share your reasoning and consider others' perspectives.",
  finalVotePrompt:
    "After discussing with your group, what is your final prediction about which group showed a larger increase in employment rates at ages 63–65 after the reform?",
  detailsTitle: "Pension reform scenario",
  detailsToggle: {
    show: "👁️ Show scenario",
    hide: "🙈 Hide scenario",
    hiddenText: "Scenario hidden. Click \"Show scenario\" to view the details again."
  },
  aiChatTitle: "AI Assistant Discussion",
  aiChatDescription:
    "Discuss your prediction with the AI assistant to clarify your thinking before joining the group. It will take 5-10 seconds for the AI assistant to respond.",
  aiWelcomeTemplate: (label) =>
    `Hello! I see you've chosen ${label}. I'm here to help you think through your reasoning about the pension reform scenario. Why did you make this prediction? What factors led you to this conclusion? Feel free to share your thoughts, and I'll ask you some questions to help you organize your thinking.`,
  confirmDescriptionTemplate: (initial) =>
    `Your initial prediction was: ${initial}. Now that you've discussed your reasoning with the AI assistant, please confirm your prediction (you may keep it the same or change it). After confirming, you will proceed to the lobby and wait for other players to form the game.`,
  options: [
    { value: "Group P", label: "Group P", shortLabel: "Group P", buttonLabel: "Group P" },
    { value: "Group D", label: "Group D", shortLabel: "Group D", buttonLabel: "Group D" }
  ],
  quizEnabled: false,
  DetailsComponent: PensionReformDetails,
};

const flatTaxTask = {
  id: "flat-tax",
  title: "Flat Tax Reform — Prediction Task",
  shortTitle: "Flat Tax Reform",
  objectiveText:
    "You will be playing this game with other participants. The objective is to make an accurate prediction, which will be determined by taking the majority vote of your group.",
  introPrompt:
    "Consider the flat tax reform scenario. What happened to total personal income tax revenue in the first full year after the reform?",
  caseNotice:
    "This is the scenario you will discuss with other participants in the upcoming discussion.",
  questionText: "What is your prediction?",
  discussionPrompt:
    "Discuss your predictions about what happened to total personal income tax revenue after the flat tax reform. Share your reasoning and consider others' perspectives.",
  finalVotePrompt:
    "After discussing with your group, what is your final prediction about what happened to total personal income tax revenue in the first full year after the reform?",
  detailsTitle: "Flat tax reform scenario",
  detailsToggle: {
    show: "👁️ Show scenario",
    hide: "🙈 Hide scenario",
    hiddenText: "Scenario hidden. Click \"Show scenario\" to view the details again."
  },
  aiChatTitle: "AI Assistant Discussion",
  aiChatDescription:
    "Discuss your prediction with the AI assistant to clarify your thinking before joining the group. It will take 5-10 seconds for the AI assistant to respond.",
  aiWelcomeTemplate: (label) =>
    `Hello! I see you've chosen ${label}. I'm here to help you think through your reasoning about the flat tax reform scenario. Why did you make this prediction? What factors led you to this conclusion? Feel free to share your thoughts, and I'll ask you some questions to help you organize your thinking.`,
  confirmDescriptionTemplate: (initial) =>
    `Your initial prediction was: ${initial}. Now that you've discussed your reasoning with the AI assistant, please confirm your prediction (you may keep it the same or change it). After confirming, you will proceed to the lobby and wait for other players to form the game.`,
  options: [
    { value: "Option A", label: "Option A", shortLabel: "Option A", buttonLabel: "Option A: Tax Revenue Declined" },
    { value: "Option B", label: "Option B", shortLabel: "Option B", buttonLabel: "Option B: Tax Revenue Increased" },
  ],
  quizEnabled: false,
  DetailsComponent: FlatTaxDetails,
};

const flatTaxMathTask = {
  id: "flat-tax-math",
  title: "Flat Tax Reform -- Revenue Calculation",
  shortTitle: "Flat Tax Revenue Calculation",
  objectiveText:
    "Your objective is to make an accurate prediction.",
  introPrompt:
    "What happened to total personal income tax revenue in the first full year after the reform?",
  caseNotice:
    "",
  questionText:
    "What happened to total personal income tax revenue in the first full year after the reform?",
  discussionPrompt:
    "Discuss your prediction about what happened to total personal income tax revenue in the first full year after the reform.",
  finalVotePrompt:
    "What happened to total personal income tax revenue in the first full year after the reform?",
  detailsTitle: "Flat tax reform scenario",
  detailsToggle: {
    show: "Show scenario",
    hide: "Hide scenario",
    hiddenText: "Scenario hidden. Click \"Show scenario\" to view the details again."
  },
  aiChatTitle: "AI Assistant Discussion",
  aiChatDescription:
    "Discuss your prediction with the AI assistant to clarify your thinking before continuing. It will take 5-10 seconds for the AI assistant to respond.",
  aiWelcomeTemplate: (label) =>
    `Hello. I see you chose ${label}. What led you to that prediction about the flat tax revenue calculation?`,
  confirmDescriptionTemplate: (initial) =>
    `Your initial prediction was: ${initial}. Now that you've completed the follow-up step, please confirm your final prediction (you may keep it the same or change it).`,
  options: [
    { value: "Increase", label: "Increase", shortLabel: "Increase", buttonLabel: "Increase" },
    { value: "Decrease", label: "Decrease", shortLabel: "Decrease", buttonLabel: "Decrease" },
  ],
  quizEnabled: false,
  DetailsComponent: FlatTaxMathDetails,
  confidenceMin: 50,
  confidenceMax: 100,
  requireWholeNumberConfidence: true,
  confidenceFieldLabel:
    "How confident are you that your answer is correct?",
  confidencePlaceholder: "50-100",
  confidenceHelpText:
    'Here, confidence means: "the probability that my chosen answer is correct." Because this question has two answer options, enter a number from 50 to 100.',
  confidenceErrorText:
    "Please enter a whole-number confidence from 50 to 100. Values below 50 are not allowed once you have selected one of two answers.",
  templateQuestionnaire: {
    introText:
      "Before confirming your prediction, please answer the questions below.",
  },
  integrityNotice:
    "Please work on your own. Do not use ChatGPT, Claude, Gemini, Copilot, or any other outside AI or LLM tool while answering this task. Copying and pasting are disabled during this task, and responses may be rejected if there is evidence for prohibited outside help.",
  disableClipboard: true,
};

function normalizeWholeNumberPredictionValue(value, min, max) {
  if (value === undefined || value === null) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < min || parsed > max || !Number.isInteger(parsed)) {
    return "";
  }
  return String(parsed);
}

function normalizeBaseRatePredictionValue(value) {
  return normalizeWholeNumberPredictionValue(value, 0, 100);
}

function formatBaseRatePredictionValue(value) {
  const normalized = normalizeBaseRatePredictionValue(value);
  return normalized ? `${normalized}%` : String(value || "").trim();
}

function normalizeFlatTaxMathOpenendPredictionValue(value) {
  return normalizeWholeNumberPredictionValue(value, 0, 1000);
}

function formatFlatTaxMathOpenendPredictionValue(value) {
  const normalized = normalizeFlatTaxMathOpenendPredictionValue(value);
  return normalized ? `$${normalized} million` : String(value || "").trim();
}

const baseRateTask = {
  id: "base-rate",
  title: "Disease Screening - Probability Judgment",
  shortTitle: "Disease Screening",
  objectiveText:
    "Your objective is to make an accurate probability judgment.",
  introPrompt:
    "A randomly selected adult receives a positive result on a screening test. What is the probability that this person actually has Veltman syndrome?",
  caseNotice:
    "",
  questionText:
    "What is the probability that this person actually has Veltman syndrome?",
  discussionPrompt:
    "Discuss your estimate of the probability that this person actually has Veltman syndrome.",
  finalVotePrompt:
    "What is your final estimate of the probability that this person actually has Veltman syndrome?",
  detailsTitle: "Disease screening scenario",
  detailsToggle: {
    show: "Show scenario",
    hide: "Hide scenario",
    hiddenText: "Scenario hidden. Click \"Show scenario\" to view the details again."
  },
  aiChatTitle: "AI Assistant Discussion",
  aiChatDescription:
    "Discuss your estimate with the following built-in AI assistant to clarify your thinking before continuing. It will take 5-10 seconds for the AI assistant to respond.",
  aiWelcomeTemplate: (value) =>
    `Hello. I see you estimated ${formatBaseRatePredictionValue(value)}. What led you to that probability judgment?`,
  confirmDescriptionTemplate: (initial) =>
    `Your initial prediction was: ${formatBaseRatePredictionValue(initial)}. Now that you've completed the follow-up step, please confirm your final prediction (you may keep it the same or change it).`,
  options: [],
  predictionInput: {
    type: "number",
    min: 0,
    max: 100,
    step: 1,
    requireWholeNumber: true,
    label:
      "Enter your answer as a whole-number percentage (0-100)",
    placeholder: "0-100",
    helpText:
      "Please write a round number.",
    errorText:
      "Please enter a whole-number percentage from 0 to 100.",
  },
  formatPredictionDisplay: formatBaseRatePredictionValue,
  normalizePredictionValue: normalizeBaseRatePredictionValue,
  quizEnabled: false,
  DetailsComponent: BaseRateDetails,
  confidenceMin: 0,
  confidenceMax: 100,
  confidenceFieldLabel:
    "How confident are you that your answer is correct? (0-100%)",
  confidencePlaceholder: "0-100",
  confidenceHelpText:
    "Confidence means the probability that your chosen answer is correct. Enter a number from 0 to 100.",
  confidenceErrorText:
    "Please enter a confidence from 0 to 100.",
  changedAnswerConfidence: {
    label:
      "Because you changed your answer, how much confidence do you still assign to your initial answer? (0-100%)",
    placeholder: "0-100",
    helpText:
      "Enter a number from 0 to 100. Your confidence in your final answer and your confidence in your initial answer should sum to 100 or less.",
    errorText:
      "Please enter a confidence from 0 to 100 for your initial answer. The two confidence values must sum to 100 or less.",
    combinedMax: 100,
  },
  // The same elicitation at the final-answer screen of the post-peer follow-up
  // arms (postPeerChangedAnswerConfidence). There the answer the participant
  // leaves behind is the estimate given right after the peer review, so the
  // label names that estimate by value; FinalVote supplies it.
  postPeerChangedAnswerConfidence: {
    labelTemplate: (previousEstimate) =>
      `Because you changed your answer, how much confidence do you still assign to your previous estimate of ${previousEstimate}? (0-100%)`,
    placeholder: "0-100",
    helpText:
      "Enter a number from 0 to 100. Your confidence in your final estimate and your confidence in your previous estimate should sum to 100 or less.",
    errorText:
      "Please enter a confidence from 0 to 100 for your previous estimate. The two confidence values must sum to 100 or less.",
    combinedMax: 100,
  },
  templateQuestionnaire: {
    introText:
      "Before confirming your prediction, please answer the questions below.",
  },
  integrityNotice:
    "Please work on your own. Do not use ChatGPT, Claude, Gemini, Copilot, or any other outside AI or LLM tool while answering this task. Copying and pasting are disabled during this task, and responses may be rejected if there is evidence for prohibited outside help.",
  disableClipboard: true,
};

const flatTaxMathOpenendTask = {
  id: "flat-tax-math-openend",
  title: "Flat Tax Reform -- Revenue Calculation",
  shortTitle: "Flat Tax Revenue Calculation",
  objectiveText:
    "Your objective is to make an accurate revenue estimate.",
  introPrompt:
    "What will be the total personal income tax revenue in the first full year after the reform?",
  caseNotice:
    "",
  questionText:
    "What will be the total personal income tax revenue in the first full year after the reform?",
  discussionPrompt:
    "Discuss your estimate of the total personal income tax revenue in the first full year after the reform.",
  finalVotePrompt:
    "What is your final estimate of the total personal income tax revenue in the first full year after the reform?",
  detailsTitle: "Flat tax reform scenario",
  detailsToggle: {
    show: "Show scenario",
    hide: "Hide scenario",
    hiddenText: "Scenario hidden. Click \"Show scenario\" to view the details again."
  },
  aiChatTitle: "AI Assistant Discussion",
  aiChatDescription:
    "Discuss your estimate with the AI assistant to clarify your thinking before continuing. It will take 5-10 seconds for the AI assistant to respond.",
  aiWelcomeTemplate: (value) =>
    `Hello. I see you estimated ${formatFlatTaxMathOpenendPredictionValue(value)}. What led you to that revenue estimate?`,
  confirmDescriptionTemplate: (initial) =>
    `Your initial prediction was: ${formatFlatTaxMathOpenendPredictionValue(initial)}. Now that you've completed the follow-up step, please confirm your final prediction (you may keep it the same or change it).`,
  options: [],
  predictionInput: {
    type: "number",
    min: 0,
    max: 1000,
    step: 1,
    requireWholeNumber: true,
    label:
      "Enter your answer as a whole-number amount in millions of dollars",
    placeholder: "0-1000",
    helpText:
      "Please write a whole number representing millions of dollars.",
    errorText:
      "Please enter a whole-number amount from 0 to 1000.",
  },
  formatPredictionDisplay: formatFlatTaxMathOpenendPredictionValue,
  normalizePredictionValue: normalizeFlatTaxMathOpenendPredictionValue,
  quizEnabled: false,
  DetailsComponent: FlatTaxMathOpenendDetails,
  confidenceMin: 0,
  confidenceMax: 100,
  confidenceFieldLabel:
    "How confident are you that your answer is correct? (0-100%)",
  confidencePlaceholder: "0-100",
  confidenceHelpText:
    "Confidence means the probability that your chosen answer is correct. Enter a number from 0 to 100.",
  confidenceErrorText:
    "Please enter a confidence from 0 to 100.",
  changedAnswerConfidence: {
    label:
      "Because you changed your answer, how much confidence do you still assign to your initial answer? (0-100%)",
    placeholder: "0-100",
    helpText:
      "Enter a number from 0 to 100. Your confidence in your final answer and your confidence in your initial answer should sum to 100 or less.",
    errorText:
      "Please enter a confidence from 0 to 100 for your initial answer. The two confidence values must sum to 100 or less.",
    combinedMax: 100,
  },
  templateQuestionnaire: {
    introText:
      "Before confirming your prediction, please answer the questions below.",
  },
  templateQuestionnaireOverrides: {
    mainReason: {
      question: "What is the main reason behind your revenue estimate?",
      placeholder: "Please describe the main reason behind your estimate.",
    },
    reasonMatters: {
      enabled: false,
    },
    oppositeReason: {
      question:
        "Can you think of one reason someone might arrive at a meaningfully different revenue estimate than yours? Please elaborate.",
      placeholder:
        "Please describe one reason someone might arrive at a meaningfully different revenue estimate.",
    },
  },
  integrityNotice:
    "Please work on your own. Do not use ChatGPT, Claude, Gemini, Copilot, or any other outside AI or LLM tool while answering this task. Copying and pasting are disabled during this task, and responses may be rejected if there is evidence for prohibited outside help.",
  disableClipboard: true,
};

const fourTasksTask = {
  id: "four-tasks",
  title: "Four-Question Prediction Task",
  shortTitle: "Four Questions",
  objectiveText:
    "Your objective is to answer four standalone prediction questions as accurately as possible.",
  introPrompt:
    "You will answer four questions presented in a random order. Each question requires an answer and a confidence rating.",
  questionText: "Answer the question and enter your confidence.",
  quizEnabled: false,
};

// Overrides for story-*nogroup* conditions: reframe all group language to individual
const storyNogroupOverrides = {
  objectiveText:
    "",
  caseNotice:
    "",
  aiChatTitle: "Converse with an AI Assistant",
  aiChatDescription:
    "The AI assistant will ask follow-up questions to help you articulate your reasoning. It will take 5-10 seconds for the AI assistant to respond.",
  aiChatCompleteHeading: "Ready to Continue?",
  aiChatCompleteDescription:
    "When you're ready, click below to confirm your prediction.",
  aiWelcomeTemplate: (label) =>
    `You chose ${label}. What led you to this prediction?`,
  confirmDescriptionTemplate: (initial) =>
    `Your initial prediction was: ${initial}. Now that you've answered the AI assistant's follow-up questions, please confirm your final prediction (you may keep it the same or change it).`,
};

const storyNogroupTemplateOverrides = {
  aiChatTitle: "Follow-up Questionnaire",
  aiChatDescription:
    "Please answer the follow-up questions to articulate your reasoning before you confirm your prediction.",
  aiChatCompleteHeading: "Ready to Continue?",
  aiChatCompleteDescription:
    "When you're ready, click below to confirm your prediction.",
  confirmDescriptionTemplate: (initial) =>
    `Your initial prediction was: ${initial}. Now that you've answered the follow-up questions, please confirm your final prediction (you may keep it the same or change it).`,
};

const alignedBaseRateTemplateTreatmentNames = new Set([
  "story-baserate-template-nopolish-syngroup",
  // Asymmetric-assignment clone of the arm above. Must stay in this set: the
  // aligned wording also disables the third template box, so omitting it would
  // silently give this arm one extra question its sibling does not ask.
  "story-baserate-template-assypeerassign",
  "story-baserate-template-groupfirst-syngroup",
  "story-baserate-template-groupfirst-syngroup-askall",
  "story-baserate-template-groupfirst-syngroup-askall-nocounter",
  // Follow-up arms. Same two questions as the main study's template arm.
  "story-baserate-template-groupfirst-followup",
  "story-baserate-template-askall-followup",
  "story-baserate-template-nopolish-extremepeer-syngroup",
  "story-baserate-template-polish-syngroup",
  "story-baserate-template-nopolish-group3",
  "story-baserate-template-polish-group3",
]);

function usesAlignedBaseRateTemplateQuestions(treatment) {
  const name = (
    treatment?.name ||
    treatment?.treatmentName ||
    treatment?.factors?.treatmentName ||
    ""
  ).toLowerCase();
  return alignedBaseRateTemplateTreatmentNames.has(name);
}

function isStoryNogroupMeta(treatment) {
  if (hasShareableGroupRationale(treatment)) {
    return false;
  }

  if (
    getTreatmentType(treatment) === "nogroup" &&
    ["template", "socratic"].includes(getPreLobbyMode(treatment))
  ) {
    return true;
  }

  const name = (
    treatment?.name ||
    treatment?.treatmentName ||
    treatment?.factors?.treatmentName ||
    ""
  ).toLowerCase();
  if (
    name.includes("story-retire-pilot-nogroup-meta") ||
    name.includes("story-retire-pilot-nogroup-devil") ||
    name.includes("story-retire-pilot-nogroup-template") ||
    name.includes("story-tax-pilot-nogroup-meta") ||
    name.includes("story-tax-pilot-nogroup-devil") ||
    name.includes("story-tax-pilot-nogroup-template") ||
    name.includes("story-tax-math-socratic-nogroup-norationale") ||
    name.includes("story-tax-math-nogroup-template") ||
    name.includes("story-tax-math-openend-socratic-nogroup-norationale") ||
    name.includes("story-tax-math-openend-nogroup-template") ||
    name.includes("story-baserate-socratic-nogroup-norationale") ||
    name.includes("story-baserate-nogroup-template")
  ) return true;
  // Fallback: story task with nogroup type is unique to this condition
  const task = treatment?.task || treatment?.factors?.task;
  const tType = treatment?.treatmentType || treatment?.factors?.treatmentType;
  return (
    ((task === "pension-reform" ||
      task === "flat-tax" ||
      task === "flat-tax-math" ||
      task === "flat-tax-math-openend" ||
      task === "base-rate")) &&
    tType === "nogroup"
  );
}

function isStoryNogroupTemplate(treatment) {
  if (hasShareableGroupRationale(treatment)) {
    return false;
  }

  if (getTreatmentType(treatment) === "nogroup" && getPreLobbyMode(treatment) === "template") {
    return true;
  }

  const name = (
    treatment?.name ||
    treatment?.treatmentName ||
    treatment?.factors?.treatmentName ||
    ""
  ).toLowerCase();
  return (
    name.includes("story-retire-pilot-nogroup-template") ||
    name.includes("story-tax-pilot-nogroup-template") ||
    name.includes("story-tax-math-nogroup-template") ||
    name.includes("story-tax-math-openend-nogroup-template") ||
    name.includes("story-baserate-nogroup-template")
  );
}

export function getTaskConfig(treatment = {}) {
  const factors = treatment?.factors || treatment || {};
  const taskId = factors.task || treatment.task;
  const preLobbyMode = getPreLobbyMode(treatment);
  let config;
  if (taskId === "pension-reform") config = pensionReformTask;
  else if (taskId === "flat-tax") config = flatTaxTask;
  else if (taskId === "flat-tax-math") config = flatTaxMathTask;
  else if (taskId === "flat-tax-math-openend") config = flatTaxMathOpenendTask;
  else if (taskId === "base-rate") config = baseRateTask;
  else if (taskId === "four-tasks") config = fourTasksTask;
  else config = loanDefaultTask;

  // Apply individual-framing overrides for story-nogroup treatments
  if (isStoryNogroupMeta(treatment)) {
    // Strip group language from finalVotePrompt
    const individualFinalVote = config.finalVotePrompt
      ? config.finalVotePrompt.replace(/^After discussing with your group, /i, "")
          .replace(/^w/, (c) => c.toUpperCase())
      : config.finalVotePrompt;
    config = { ...config, ...storyNogroupOverrides, finalVotePrompt: individualFinalVote };
    if (isStoryNogroupTemplate(treatment)) {
      config = { ...config, ...storyNogroupTemplateOverrides };
    }
  }

  if (isStoryNogroupTemplate(treatment) && config.templateQuestionnaireOverrides) {
    config = {
      ...config,
      templateQuestionnaire: {
        ...(config.templateQuestionnaire || {}),
        ...(config.templateQuestionnaireOverrides || {}),
      },
    };
  }

  if (usesAlignedBaseRateTemplateQuestions(treatment)) {
    config = {
      ...config,
      templateQuestionnaire: {
        ...(config.templateQuestionnaire || {}),
        mainReason: {
          ...(config.templateQuestionnaire?.mainReason || {}),
          question: "What is the main reason behind your prediction?",
        },
        reasonMatters: {
          ...(config.templateQuestionnaire?.reasonMatters || {}),
          enabled: false,
        },
        oppositeReason: {
          ...(config.templateQuestionnaire?.oppositeReason || {}),
          question: "What would someone who disagrees say?",
        },
      },
    };
  }

  if (
    (taskId === "flat-tax-math" ||
      taskId === "flat-tax-math-openend" ||
      taskId === "base-rate") &&
    isPasteEnabledNogroupCondition(treatment)
  ) {
    config = {
      ...config,
      integrityNotice:
        "Please work on your own. Do not use ChatGPT, Claude, Gemini, Copilot, or any other outside AI or LLM tool while answering this task. Responses may be rejected if there is evidence for prohibited outside help.",
      disableClipboard: false,
    };
  }

  if (
    isBaseRateGroup3Condition(treatment) ||
    isBaseRateStaticSyntheticGroupCondition(treatment)
  ) {
    const followUpStepLabel =
      preLobbyMode === "template"
        ? "the follow-up questionnaire"
        : preLobbyMode === "none"
          ? "the task"
          : "the AI assistant discussion";
    const reviewSourceLabel =
      preLobbyMode === "template"
        ? "your answers"
        : preLobbyMode === "none"
          ? "your estimate"
          : "your prior dialog";
    const hasPostConfirmPolishStep = Boolean(getPostConfirmPolishMode(treatment));
    const skipsPostConfirmWriteup = suppressesPostConfirmWriteup(treatment);
    const isStaticSyntheticGroup =
      isBaseRateStaticSyntheticGroupCondition(treatment);
    // In the post-peer conditions the reflection activity runs after the peer
    // review and immediately before the final estimate, so the copy that
    // describes the sequence has to say so. The two arms share every sentence
    // below and differ only in the noun for the activity itself.
    const isPostPeerTiming =
      isStaticSyntheticGroup &&
      getReflectionTiming(treatment) === "post-peer" &&
      preLobbyMode !== "none";
    // The askall pair discusses all three answers in the activity, so the
    // clause naming the activity has to say so; the other post-peer
    // conditions keep their original wording.
    const postPeerAskAllClause =
      treatment?.postPeerAskAll === true ||
      treatment?.factors?.postPeerAskAll === true;
    const postPeerActivityClause =
      preLobbyMode === "template"
        ? postPeerAskAllClause
          ? "answering the follow-up questions about the three answers"
          : "answering the follow-up questions"
        : postPeerAskAllClause
          ? "discussing the three answers with the AI assistant"
          : "discussing your reasoning with the AI assistant";
    // "What is your final estimate ..." -> "what is your final estimate ...",
    // so it can follow an introductory clause.
    const finalVoteQuestionClause = config.finalVotePrompt
      ? config.finalVotePrompt.charAt(0).toLowerCase() + config.finalVotePrompt.slice(1)
      : "";
    config = {
      ...config,
      ...(isStaticSyntheticGroup
        ? {
            studyObjectiveText: isPostPeerTiming
              ? "This experiment studies how reflection affects an individual's decision in a group context."
              : "This experiment studies how preparation affects an individual's decision in a group context.",
            groupMindsetPrompt:
              "As you work through the study, think about how you would combine your own judgment with input from others when reaching a decision.",
            initialPredictionDescription: "Task Description",
          }
        : {
            objectiveText:
              "You will be playing this game with two other participants. The objective is to make an accurate probability judgment, which will be determined by the majority vote of your 3-person group.",
          }),
      caseNotice:
        isStaticSyntheticGroup
          ? ""
          : "This is the scenario you will discuss with your group in the upcoming discussion.",
      discussionPrompt:
        isStaticSyntheticGroup
          ? ""
          : config.discussionPrompt,
      groupReviewHeadline:
        isStaticSyntheticGroup
          ? "Imagine you are making a group decision with two peers."
          : "",
      groupReviewSubtext:
        isStaticSyntheticGroup
          ? "Read their estimates and reasoning below. Think about how you would weigh their views against your own, what you find most persuasive, and what answer you would reach if you were deciding together as a group of three."
          : "",
      detailsTitle: isStaticSyntheticGroup ? "Task Description" : config.detailsTitle,
      detailsToggle: isStaticSyntheticGroup
        ? {
            show: "Show task",
            hide: "Hide task",
            hiddenText: 'Task hidden. Click "Show task" to view the details again.',
          }
        : config.detailsToggle,
      finalVotePrompt:
        isPostPeerTiming
          ? `After reviewing the other participants' responses and ${postPeerActivityClause}, ${finalVoteQuestionClause}`
          : isStaticSyntheticGroup
            ? "After reviewing the other participants' responses, what is your final estimate of the probability that this person actually has Veltman syndrome?"
            : config.finalVotePrompt,
      // Sequence copy for the reflection activity itself. The template arm reads
      // this through templateQuestionnaire.introText and the Socratic arm
      // through aiChatDescription / aiChatCompleteDescription; the post-peer
      // wording is the same sentence in both.
      ...(isPostPeerTiming
        ? {
            templateQuestionnaire: {
              ...(config.templateQuestionnaire || {}),
              introText:
                "Before you give your final estimate, please answer the questions below.",
            },
            aiChatDescription:
              "Before you give your final estimate, discuss your estimate with the following built-in AI assistant to clarify your thinking. It will take 5-10 seconds for the AI assistant to respond.",
            aiChatCompleteDescription:
              "When you're satisfied with your discussion with the AI assistant, click below to proceed to your final estimate.",
          }
        : {}),
      confirmDescriptionTemplate: (initial) => {
        const opening = `Your initial prediction was: ${formatBaseRatePredictionValue(initial)}. Now that you've completed ${followUpStepLabel}, please confirm your prediction (you may keep it the same or change it).`;
        if (isStaticSyntheticGroup) {
          // These arms have no write-up step left to announce: the rationale
          // is either collected on the initial screen or not collected at all.
          if (skipsPostConfirmWriteup) {
            return `${opening} After confirming, you will review the other participants' responses before moving to your final estimate.`;
          }
          if (hasPostConfirmPolishStep) {
            return `${opening} After confirming, you will write a rationale, review an AI-polished version, and then review the other participants' responses before moving to your final estimate.`;
          }
          return `${opening} After confirming, you will write a rationale and then review the other participants' responses before moving to your final estimate.`;
        }
        return `${opening} After confirming, you will review ${reviewSourceLabel} and write a rationale to share with your group${hasPostConfirmPolishStep ? ", then review an AI-polished version before choosing the final text the group will see." : "."}`;
      },
    };
  }

  return config;
}
